import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  SpotlightContainer,
  SpotlightHole,
  SpotlightMask,
  SpotlightOverlay,
} from './styles';

interface OnboardingSpotlightProps {
  targetSelector: string | null;
  isActive: boolean;
  onOverlayClick: () => void;
  padding?: number;
  pulseAnimation?: boolean;
  shape?: 'circle' | 'rectangle';
  allowTargetClick?: boolean;
}

const OnboardingSpotlight: React.FC<OnboardingSpotlightProps> = ({
  targetSelector,
  isActive,
  onOverlayClick,
  padding = 5,
  pulseAnimation = true,
  shape = 'rectangle',
  allowTargetClick = false,
}) => {
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [pageLoaded, setPageLoaded] = useState(
    document.readyState === 'complete',
  );
  const [elementFound, setElementFound] = useState(false);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const targetElementRef = useRef<Element | null>(null);

  useEffect(() => {
    const handleResize = (): void => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    const handleLoad = (): void => {
      setPageLoaded(true);
    };

    window.addEventListener('resize', handleResize);

    if (!pageLoaded) {
      window.addEventListener('load', handleLoad);
    }

    window.addEventListener('scroll', updatePosition);
    return (): void => {
      window.removeEventListener('resize', handleResize);
      if (!pageLoaded) {
        window.removeEventListener('load', handleLoad);
      }
      window.removeEventListener('scroll', updatePosition);
    };
  }, [pageLoaded]);

  const updatePosition = useCallback((): void => {
    if (!targetSelector || !pageLoaded) {
      setElementFound(false);
      return;
    }

    const targetElement = document.querySelector(targetSelector);
    targetElementRef.current = targetElement;

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();

      setPosition({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      setElementFound(true);
    } else {
      setElementFound(false);
    }
  }, [padding, pageLoaded, targetSelector]);

  // adicionar a posição do elemento alvo ao clicar no spotlight
  useEffect(() => {
    if (!isActive || !pageLoaded) return;

    // Sempre executar updatePosition, mesmo sem targetSelector
    updatePosition();

    // Se não tem targetSelector, não precisa observer
    if (!targetSelector) {
      return;
    }

    // Observer para detectar mudanças no DOM que podem afetar a posição
    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Atualiza periodicamente para garantir precisão (reduzido para 1s para melhor performance)
    const interval = setInterval(updatePosition, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [
    targetSelector,
    isActive,
    padding,
    windowSize,
    pageLoaded,
    updatePosition,
  ]);

  const handleClick = (e: React.MouseEvent): void => {
    // Verificar se o clique foi dentro do "buraco" do spotlight
    if (spotlightRef.current && allowTargetClick && elementFound) {
      const spotlightRect = spotlightRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Se o clique foi dentro do spotlight, ignoramos o evento para permitir clicar no elemento real
      if (allowTargetClick) {
        if (
          mouseX >= spotlightRect.left &&
          mouseX <= spotlightRect.right &&
          mouseY >= spotlightRect.top &&
          mouseY <= spotlightRect.bottom
        ) {
          // Disparar clique no elemento alvo
          if (
            targetElementRef.current &&
            targetElementRef.current instanceof HTMLElement
          ) {
            targetElementRef.current.click();
          }
          return;
        }
      }
    }

    // Se o clique foi fora do spotlight, chamar o callback
    onOverlayClick();
  };

  // Só não renderizar se não estiver ativo
  if (!isActive) return null;

  return createPortal(
    <SpotlightContainer onClick={handleClick}>
      {/* Renderizar máscara apenas quando não há targetSelector */}
      {!targetSelector && <SpotlightMask />}

      {/* Só renderizar o buraco se houver targetSelector e elemento encontrado */}
      {targetSelector && elementFound && (
        <>
          <SpotlightOverlay
            style={{
              clipPath: `path('M0,0 L0,${window.innerHeight} L${
                window.innerWidth
              },${window.innerHeight} L${window.innerWidth},0 Z M${
                position.left
              },${position.top} L${position.left + position.width},${
                position.top
              } L${position.left + position.width},${
                position.top + position.height
              } L${position.left},${position.top + position.height} Z')`,
            }}
          />
          <SpotlightHole
            ref={spotlightRef}
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              height: position.height,
              borderRadius: shape === 'circle' ? '50%' : '0',
            }}
            className={pulseAnimation ? 'pulse' : ''}
          />
        </>
      )}
    </SpotlightContainer>,
    document.body,
  );
};

export default OnboardingSpotlight;
